import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { JobDetail } from '@nest-recruit/shared';
import { errorMessage } from '../../../../lib/api-client';
import { useToasts } from '../../../../stores/toasts';
import { attachJobFile, jobKey, removeJobFile } from '../../api/jobs-api';
import { useSaveLane } from '../../hooks/save-lane';

// F5.20 (R3): a job's files go through the builder's save lane like every other save, because
// replacing the advert image moves the job's version on and the next section save must know.

const UNDO_MS = 10_000;

export function useAttachJobFile(job: JobDetail): {
  readonly attach: (file: File, target: { readonly sectionKey: string } | { readonly isImage: true }) => Promise<void>;
  readonly isAttaching: boolean;
} {
  const lane = useSaveLane();
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const [isAttaching, setIsAttaching] = useState(false);

  const attach = useCallback(
    async (file: File, target: { readonly sectionKey: string } | { readonly isImage: true }): Promise<void> => {
      setIsAttaching(true);
      try {
        const send = (): Promise<JobDetail> => attachJobFile(job.id, file, target);
        const updated = lane ? await lane.run(send) : await send();
        queryClient.setQueryData(jobKey(job.id), updated);
        show({ tone: 'success', message: `${file.name} attached` });
      } catch (error) {
        show({ tone: 'danger', message: `${file.name} wasn't attached`, detail: errorMessage(error) });
      } finally {
        setIsAttaching(false);
      }
    },
    [job.id, lane, queryClient, show],
  );
  return { attach, isAttaching };
}

/**
 * Undo beats confirm (CLAUDE.md §5a rule 8): a removed file disappears at once and is only
 * removed on the server once the ten-second undo has passed — or straight away if the person
 * leaves the section first, so nothing is left half done.
 */
export function useRemoveJobFile(job: JobDetail): { readonly hidden: ReadonlySet<string>; readonly remove: (documentId: string, fileName: string) => void } {
  const lane = useSaveLane();
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const showUndo = useToasts((toasts) => toasts.showUndo);
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const send = useCallback(
    async (documentId: string): Promise<void> => {
      timers.current.delete(documentId);
      try {
        const run = (): Promise<JobDetail> => removeJobFile(job.id, documentId);
        const updated = lane ? await lane.run(run) : await run();
        queryClient.setQueryData(jobKey(job.id), updated);
      } catch (error) {
        setHidden((current) => {
          const next = new Set(current);
          next.delete(documentId);
          return next;
        });
        show({ tone: 'danger', message: "The file wasn't removed", detail: errorMessage(error) });
      }
    },
    [job.id, lane, queryClient, show],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const [documentId, timer] of pending) {
        clearTimeout(timer);
        void send(documentId);
      }
    };
  }, [send]);

  const remove = useCallback(
    (documentId: string, fileName: string): void => {
      setHidden((current) => new Set(current).add(documentId));
      timers.current.set(
        documentId,
        setTimeout(() => void send(documentId), UNDO_MS),
      );
      showUndo(`${fileName} removed`, () => {
        clearTimeout(timers.current.get(documentId));
        timers.current.delete(documentId);
        setHidden((current) => {
          const next = new Set(current);
          next.delete(documentId);
          return next;
        });
      });
    },
    [send, showUndo],
  );

  return { hidden, remove };
}
